import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
    Table,
    InputNumber,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { WoodType } from "../../../../types/WoodType";
import { useNavigate } from 'react-router-dom';

interface WoodModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void;
    initialValues?: any;
    isEditMode?: boolean;
    woodTypesData?: WoodType[];
    ledgerData?: { ledger_id: number; ledger_name: string; company_name: string }[];
}

interface GoodsRow {
    key: string;
    description: string;
    hsn: string;
    qty: number;
    rate: number;
    amount: number;
}

interface Ledger {
    ledger_id: number;
    ledger_deals_in_company?: number;
    ledger_status?: number;
    ledger_first_name: string;
    ledger_last_name?: string;
    ledger_mobile_no?: string;
    ledger_company_name?: string;
    ledger_gstin?: string;
}
interface Wood {
    wood_id: number;
    wood_name: string;
}
const AddPO: React.FC<WoodModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
    woodTypesData = [],
    ledgerData = [],
}) => {
    const [form] = Form.useForm();
    const [goodsData, setGoodsData] = useState<GoodsRow[]>([]);
    const [isModalClose, setIsModalClose] = useState(true);
    const [woodList, setWoodList] = useState<Wood[]>([]);

    const [ledgers, setLedgers] = useState<Ledger[]>([]);

    const [dropdownOptions, setDropdownOptions] = useState<{ label: string; value: number }[]>([]);
    const navigate = useNavigate();

    const fetchWoodList = async () => {
        try {
            const res = await apiRequest({
                method: "get",
                url: "woods", // your API endpoint
            });

            setWoodList(res || []);
        } catch (error) {
            showError("Failed to load wood list");
        }
    };
    useEffect(() => {
        if (visible && initialValues) {
            form.setFieldsValue(initialValues);
        } else if (visible) {
            form.resetFields();
            setGoodsData([]);
        }
        fetchLedger()
        
        fetchWoodList();
    }, [visible, initialValues]);

    const addGoodsRow = () => {
        setGoodsData(prev => [
            ...prev,
            {
                key: Date.now().toString(),
                description: '',
                hsn: '',
                qty: 0,
                rate: 0,
                amount: 0,
            },
        ]);
    };

    const handleGoodsChange = (value: any, key: string, field: keyof GoodsRow) => {
        const updated = goodsData.map(item => {
            if (item.key === key) {
                const newItem = { ...item, [field]: value };
                newItem.amount = newItem.qty * newItem.rate;
                return newItem;
            }
            return item;
        });
        setGoodsData(updated);
    };

    const goodsColumns = [
        {
            title: 'Sr',
            dataIndex: 'sr',
            render: (_: any, __: any, index: number) => index + 1,
        },
        {
            title: 'Description of Goods',
            dataIndex: 'description',
            render: (_: any, record: GoodsRow) => (
                <Select
                    showSearch
                    placeholder="Select wood"
                    value={record.description}
                    onChange={value => handleGoodsChange(value, record.key, "description")}
                    options={woodList.map(w => ({
                        label: w.wood_name,
                        value: w.wood_name, // or w.wood_id if needed
                    }))}
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                />
            ),
        },
        {
            title: 'Qty',
            dataIndex: 'qty',
            render: (_: any, record: GoodsRow) => (
                <InputNumber
                    min={0}
                    value={record.qty}
                    onChange={value => handleGoodsChange(value || 0, record.key, 'qty')}
                />
            ),
        },
        {
            title: 'Rate',
            dataIndex: 'rate',
            render: (_: any, record: GoodsRow) => (
                <InputNumber
                    min={0}
                    value={record.rate}
                    onChange={value => handleGoodsChange(value || 0, record.key, 'rate')}
                />
            ),
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            render: (_: any, record: GoodsRow) => <span>{record.amount.toFixed(2)}</span>,
        },
        {
            title: 'Action',
            dataIndex: 'action',
            render: (_: any, record: GoodsRow) => (
                <Button
                    type="link"
                    danger
                    onClick={() => handleRemoveGoodsRow(record.key)}
                >
                    Remove
                </Button>
            ),
        },
    ];
    const handleRemoveGoodsRow = (key: string) => {
        const updatedData = goodsData.filter(item => item.key !== key);
        setGoodsData(updatedData);
    };
    const totalAmount = goodsData.reduce((sum, row) => sum + row.amount, 0);
    const cgst = totalAmount * 0.09;
    const sgst = totalAmount * 0.09;
    const grandTotal = totalAmount + cgst + sgst;

    const handleSubmit = async (values: any) => {
        const payload = {
            ...values,
            goods: goodsData,
            total_amount: totalAmount,
            cgst,
            sgst,
            grand_total: grandTotal,
        };

        try {
            const method = isEditMode ? 'put' : 'post';
            const url = isEditMode ? 'create-new-hpo' : 'create-new-hpo';
            const res = await apiRequest({
                method,
                url,
                data: payload,
            });

            showSuccess(isEditMode ? 'Wood updated successfully!' : 'Wood created successfully!');
            form.resetFields();
            setGoodsData([]);
            onCreate(res);

            if (isModalClose) {
                navigate(`/party-napi/${res?.id || 1}`); // Replace with actual ID
            }
        } catch (err) {
            showError('Failed to submit');
        }
    };

    const fetchLedger = async (mobile?: string) => {
        try {
            let url = `ledger-data`;
            if (mobile) url += `?mobile=${encodeURIComponent(mobile)}`;

            const response = await apiRequest<{ ledgers: Ledger[] }>({
                method: 'get',
                url,
            });
            console.log("ledger", response);
            const fetched = response.ledgers || [];
            setLedgers(fetched);

            // Map to dropdown options
            const options = fetched.map((ledger) => ({
                label: `${ledger.ledger_first_name} ${ledger.ledger_last_name || ''}`,
                value: ledger.ledger_id,
            }));

            setDropdownOptions(options);

            // Auto-select if only one match
            if (options.length === 1) {
                form.setFieldsValue({
                    handover_full_name: options[0].value,
                });
            } else {
                form.setFieldsValue({ handover_full_name: undefined });
            }

        } catch (error) {
            showError('Failed to fetch Ledger');
            console.error(error);
        }
    };
    return (
        <Modal
            title={isEditMode ? 'Edit Wood PO' : 'Add New Wood PO'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="95%"
            destroyOnClose
        >
            <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="wood_type"
                            label="Wood Type"
                            rules={[{ required: true }]}
                        >
                            <Select
                                showSearch
                                options={woodTypesData.map(w => ({
                                    label: w.wood_name,
                                    value: w.wood_id,
                                }))}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                placeholder="Select Wood Type"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="ledger_id" label="Company Name" rules={[{ required: true }]}>
                            <Select
                                options={ledgers.map(l => ({
                                    label: `${l.ledger_first_name} ${l.ledger_last_name} `,
                                    value: l.ledger_id,
                                }))}
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                showSearch
                                placeholder="Select Company"
                            />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="bill_no" label="Bill No" rules={[{ required: true }]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="challan_no" label="Challan No">
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="bill_date" label="Bill Date">
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="motor_vehicle_no" label="Motor Vehicle No">
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="freight_outward" label="Freight Outward Amount">
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Goods Table */}
                <Table
                    dataSource={goodsData}
                    columns={goodsColumns}
                    pagination={false}
                    footer={() => (
                        <Button type="dashed" icon={<PlusOutlined />} onClick={addGoodsRow}>
                            Add Row
                        </Button>
                    )}
                />

                {/* Totals */}
                <Row gutter={16} style={{ marginTop: 20 }}>
                    <Col span={6}>
                        <strong>Total Amount:</strong> ₹{totalAmount.toFixed(2)}
                    </Col>
                    <Col span={6}>
                        <strong>CGST (9%):</strong> ₹{cgst.toFixed(2)}
                    </Col>
                    <Col span={6}>
                        <strong>SGST (9%):</strong> ₹{sgst.toFixed(2)}
                    </Col>
                    <Col span={6}>
                        <strong>Grand Total:</strong> ₹{grandTotal.toFixed(2)}
                    </Col>
                </Row>

                {/* Buttons */}
                <Form.Item style={{ textAlign: 'right', marginTop: 30 }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && (
                            <Button
                                type="primary"
                                htmlType="submit"
                                onClick={() => setIsModalClose(false)}
                            >
                                Submit & Create New
                            </Button>
                        )}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AddPO;
